import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { notificationsApi } from '../api';
import { queryKeys } from '../../../api/queryKeys';

const REFETCH_INTERVAL_MS = 30 * 1000;

/**
 * Shared hook for the notification bell.
 *
 * - scope 'user': fetches the user's inbox + unread count and exposes
 *   markRead / markAllRead mutations.
 * - scope 'admin' | 'operator': fetches the list of recently sent
 *   notifications (monitoring view, no unread semantics).
 */
export const useNotifications = (scope = 'user') => {
    const queryClient = useQueryClient();
    const isUserScope = scope === 'user';

    const listQuery = useQuery({
        queryKey: queryKeys.notifications.list(scope),
        queryFn: () => (isUserScope
            ? notificationsApi.getMy({ channel: 'in_app', limit: 8 })
            : notificationsApi.getSentList(scope, { limit: 8 })),
        refetchInterval: REFETCH_INTERVAL_MS,
    });

    const unreadQuery = useQuery({
        queryKey: queryKeys.notifications.unread(scope),
        queryFn: () => notificationsApi.getUnreadCount(),
        enabled: isUserScope,
        refetchInterval: REFETCH_INTERVAL_MS,
    });

    const markReadMutation = useMutation({
        mutationFn: (id) => notificationsApi.markRead(id),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(scope) });
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unread(scope) });
        },
    });

    const markAllReadMutation = useMutation({
        mutationFn: () => notificationsApi.markAllRead(),
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.list(scope) });
            queryClient.invalidateQueries({ queryKey: queryKeys.notifications.unread(scope) });
        },
    });

    const data = listQuery.data || { messages: [], total: 0, unreadCount: 0 };

    return {
        notifications: data.messages || [],
        total: data.total || 0,
        unreadCount: unreadQuery.data || 0,
        loading: listQuery.isLoading,
        error: listQuery.isError ? 'خطا در دریافت اعلان‌ها' : null,
        refresh: () => {
            listQuery.refetch();
            if (isUserScope) unreadQuery.refetch();
        },
        markRead: markReadMutation.mutate,
        markAllRead: markAllReadMutation.mutate,
        markingRead: markReadMutation.isPending,
    };
};

export default useNotifications;